<template>
  <v-data-table
    :headers="headers"
    :items="csvData"
    dense
    item-key="id"
    class="elevation-1"
    :loading="showCsvDataLoading"
    calculate-widths
  >
    <template v-slot:progress>
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
    </template>
    <template v-slot:top>
      <v-expansion-panels class="mb-12">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="font-weight-bold">CSV Stats</span>
            <span class="font-weight-light ml-3">Record Count: {{ csvRecordCount }}</span>
            <span class="font-weight-light ml-3" :class="csvErrorCountColor">
              Errors: {{ csvErrorsCount }}
            </span>
            <span class="font-weight-light ml-3">Note: Table view is limited to 100 Rows</span>
            <csv-upload type="button" buttonText="Upload Different CSV" />
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- ERRORS -->
            <v-card v-if="hasCsvErrors">
              <v-toolbar color="red" dark>
                <v-toolbar-title>CSV File Errors</v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
              <v-list two-line>
                <template v-for="(error,errIdx) in csvParseErrors">
                  <v-list-item :key="errIdx">
                    <v-list-item-content>
                      <v-list-item-title>{{ error.message}}</v-list-item-title>
                      <v-list-item-subtitle>Row:{{ error.row }} | Type:{{ error.type }} | Code:{{ error.code }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-list>
            </v-card>
            <!-- STATS -->
            <v-card>
              <v-toolbar color="grey" dark>
                <v-toolbar-title>CSV File Stats</v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
              <v-list two-line>
                <template v-for="(stat,sIdx) in csvStats">
                  <v-list-item :key="sIdx" v-if="!stat.hide">
                    <v-list-item-content>
                      <v-list-item-title>{{ stat.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ stat.value }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <!-- <v-divider></v-divider> -->
                </template>
              </v-list>
            </v-card>
         </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </v-data-table>
</template>

<script>
import { mapState } from 'vuex'
import CsvUpload from './Upload'
import Utils from '@/utils'
const Papa = require('papaparse')
const R = require('ramda')

function csvParseConfig (vueInstance) {
  const boundThis = vueInstance
  return {
    delimiter: '', // auto-detect
    newline: '', // auto-detect
    quoteChar: '"',
    escapeChar: '"',
    header: false,
    trimHeaders: true,
    dynamicTyping: false,
    preview: 101, // papaparse seems to count the header as 1 - limit to only reading first N records
    encoding: '',
    worker: false,
    comments: false,
    step: undefined,
    complete: function (results, file) {
      boundThis.$logger.trace('CSV Parse Complete!')
      boundThis.parseResults = results
    },
    error: function (error, file) {
      if (error) {
        boundThis.$logger.error('Error Parsing CSV', error.message)
        boundThis.$flash({
          showClose: true,
          message: 'Error Parsing CSV! ' + error.message
        })
      }
    },
    download: false,
    skipEmptyLines: false,
    chunk: undefined,
    fastMode: undefined,
    beforeFirstChunk: undefined,
    withCredentials: undefined,
    transform: undefined
  }
}

export default {
  name: 'csv-preview',
  components: {
    [CsvUpload.name]: CsvUpload
  },
  data: function () {
    return {
      showCsvDataLoading: false,
      parseResults: {}
    }
  },
  computed: {
    ...mapState('CsvMappingStore', {
      uploadedFile: 'csvFile',
      uploadedFileStats: 'csvFileMeta',
      csvStats: 'csvStats',
      csvColumnNames: 'csvColumnNames'
    }),
    headers () {
      return this.csvColumnNames.map(col => {
        return { text: col, value: col }
      })
    },
    csvData () {
      const _rows = Utils.nestedPropertyOr([], ['data'], this.parseResults)
      const rowList = []
      for (let rowIdx = 1; rowIdx < _rows.length; rowIdx++) {
        let rowObject = {}
        for (let colIdx = 0; colIdx < _rows[rowIdx].length; colIdx++) {
          rowObject[Utils.nestedPropertyOr('Unknown', [colIdx, 'value'], this.headers)] = Utils.nestedPropertyOr('', [rowIdx, colIdx], _rows).toString()
        }
        rowList.push(rowObject)
      }
      return rowList
    },
    csvRecordCount () {
      const _rows = Utils.nestedPropertyOr([], ['data'], this.parseResults)
      return _rows.length - 1 // -1 is because we do not parse headers
    },
    hasCsvErrors () {
      return (
        Utils.nestedPropertyOr([], ['errors'], this.parseResults).length > 0
      )
    },
    csvErrorsCount () {
      const _errors = Utils.nestedPropertyOr([], ['errors'], this.parseResults)
      return _errors.length
    },
    csvErrorCountColor () {
      const _errors = Utils.nestedPropertyOr([], ['errors'], this.parseResults)
      if (_errors.length > 0) {
        return 'red--text'
      } else {
        return ''
      }
    },
    csvParseErrors () {
      return Utils.nestedPropertyOr([], ['errors'], this.parseResults)
    }
  },
  watch: {
    uploadedFile: function (newFile, oldFile) {
      if (newFile && oldFile !== newFile) {
        this.$logger.trace('Updating CSV...')
        this.parseCsv()
      }
    },
    parseResults: function (newVal, oldVal) {
      this.$store.dispatch('CsvMappingStore/updateMapping', {
        csvColumnNames: Utils.nestedPropertyOr([], ['data', 0], newVal),
        csvStats: [
          {
            name: 'Name',
            key: 'original_filename',
            value: R.pathOr('', ['original_name'], this.uploadedFileStats)
          },
          {
            name: 'Type',
            key: 'mime_type',
            value: R.pathOr('', ['type'], this.uploadedFileStats)
          },
          {
            name: 'Size',
            key: 'filesize',
            value: this.bytesToSize(R.pathOr(0, ['size'], this.uploadedFileStats))
          },
          {
            name: 'Last Modified',
            key: 'original_file_last_modified',
            value: this.asDateTime(
              R.path(['lastModifiedDate'], this.uploadedFileStats)
            )
          },
          {
            name: 'Csv Delimiter',
            key: 'csv_delimiter',
            value: this.csvDelimiter()
          },
          {
            name: 'delimiter',
            key: 'delimiter',
            value: R.path(['meta', 'delimiter'], this.parseResults),
            hide: true
          },
          { name: 'Row Count', key: 'row_count', value: this.csvRecordCount },
          { name: 'Error Count', key: 'error_count', value: this.csvErrorsCount }
        ]
      })
    }
  },
  mounted: function () {
    this.parseCsv()
  },
  methods: {
    parseCsv () {
      if (this.uploadedFile) {
        this.showCsvDataLoading = true
        const that = this
        const reader = new FileReader()
        reader.onload = function (fileLoadedEvent) {
          Papa.parse(fileLoadedEvent.target.result, csvParseConfig(that))
        }
        reader.readAsText(this.uploadedFile)
        this.showCsvDataLoading = false
      }
    },
    bytesToSize (bytes) {
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      if (bytes === 0) return 'n/a'
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10)
      if (i === 0) return `${bytes} ${sizes[i]}`
      return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`
    },
    asDateTime (val) {
      try {
        return val.toString()
      } catch (e) {
        return 'N/A'
      }
    },
    csvDelimiter () {
      return Utils.lookupCharName(
        R.pathOr('N/A', ['meta', 'delimiter'], this.parseResults)
      )
    }
  }
}
</script>
